import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import { AdicionarItemPedidoRequest, FecharMesaRequest, MesaAbertaDto, RegistrarPedidoRequest } from '../types';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../utils/errorHandler';

export const useRegistrarPedido = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: RegistrarPedidoRequest) => {
      const response = await api.post('/pedidos', request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos-do-dia'] });
      queryClient.invalidateQueries({ queryKey: ['mesas-abertas'] });
      toast.success('Pedido registrado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao registrar pedido'));
    },
  });
};

export const useListarMesasAbertas = () => {
  return useQuery<MesaAbertaDto[]>({
    queryKey: ['mesas-abertas'],
    queryFn: async () => {
      const response = await api.get('/pedidos/mesas-abertas');
      return response.data;
    },
    refetchInterval: 30000,
  });
};

export const useAdicionarItemPedido = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pedidoId, request }: { pedidoId: number; request: AdicionarItemPedidoRequest }) => {
      const response = await api.post(`/pedidos/${pedidoId}/itens`, request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mesas-abertas'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos-do-dia'] });
      toast.success('Item adicionado!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao adicionar item'));
    },
  });
};

export const useRemoverItemPedido = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pedidoId, itemId }: { pedidoId: number; itemId: number }) => {
      const response = await api.delete(`/pedidos/${pedidoId}/itens/${itemId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mesas-abertas'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos-do-dia'] });
      toast.success('Item removido!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao remover item'));
    },
  });
};

export const useFecharMesa = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pedidoId, request }: { pedidoId: number; request: FecharMesaRequest }) => {
      const response = await api.post(`/pedidos/${pedidoId}/fechar-mesa`, request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mesas-abertas'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos-do-dia'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Mesa fechada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao fechar mesa'));
    },
  });
};

export const useAtualizarDescontoPedido = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pedidoId, desconto }: { pedidoId: number; desconto: number }) => {
      const response = await api.put(`/pedidos/${pedidoId}/desconto`, { desconto });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mesas-abertas'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos-do-dia'] });
      toast.success('Desconto atualizado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar desconto'));
    },
  });
};

export const useCancelarPedido = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pedidoId: number) => {
      const response = await api.post(`/pedidos/${pedidoId}/cancelar`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mesas-abertas'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos-do-dia'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Pedido cancelado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao cancelar pedido'));
    },
  });
};
